// ===============================================================
// ✅ NotificationsPage.js — LIVORA FINAL READY
// - follow / visit / gift / withdraw / support / match
// - targeted user avatar resolution (cached by uid)
// - deepLink + meta.deepLink supported
// ===============================================================

import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  collection,
  doc,
  getDoc,
  onSnapshot,
  orderBy,
  query,
  updateDoc,
  where,
} from "firebase/firestore";
import { getDownloadURL, ref as storageRef } from "firebase/storage";
import {
  FaBell,
  FaCheckDouble,
  FaCoins,
  FaCog,
  FaComment,
  FaGift,
  FaHeadset,
  FaHeart,
  FaPhone,
  FaTh,
  FaUserFriends,
  FaEye,
} from "react-icons/fa";
import { auth, db, storage } from "../firebase";
import { formatDateTimeEn, formatNumberEn } from "../utils/digits";
import BottomNav from "../components/BottomNav";
import { defaultAvatarByGender } from "../utils/defaultAvatar";
import { SUPPORTED_NOTIFICATION_TYPES } from "../notifications/notificationTypes";
import { markNotificationsReadBatched } from "../notifications/markNotificationsRead";
import { buildExistingChatRoute } from "../navigation/nativeChatRouteBuilder";
import "./NotificationsPage.css";

const TABS = [
  { key: "all", defaultValue: "All", Icon: FaTh },
  { key: "visit", defaultValue: "Visits", Icon: FaEye },
  { key: "follow", defaultValue: "Followers", Icon: FaUserFriends },
  { key: "gift", defaultValue: "Gifts", Icon: FaGift },
  { key: "withdraw", defaultValue: "Withdrawals", Icon: FaCoins },
  { key: "support", defaultValue: "Support", Icon: FaHeadset },
];

/**
 * Client-side filter only — Firestore query unchanged. Backed by the
 * shared list in notificationTypes.js so this page and BottomNav's unread
 * badge can never drift apart again (see that file for why "message"/
 * "call" are included here).
 */
const ALLOWED_TYPES = SUPPORTED_NOTIFICATION_TYPES;

function isHttpUrl(v) {
  return typeof v === "string" && /^https?:\/\//i.test(v);
}

async function fetchUserAvatarUrl(uid) {
  if (!uid || typeof uid !== "string") return "";
  try {
    const snap = await getDoc(doc(db, "publicProfiles", uid));
    if (!snap.exists()) return "";
    const d = snap.data() || {};
    const raw = d.profileImage || d.photoURL || "";
    if (!raw || typeof raw !== "string") return "";
    if (isHttpUrl(raw) && !raw.includes("firebasestorage.googleapis.com")) {
      return raw;
    }
    if (isHttpUrl(raw)) return raw;
    try {
      return await getDownloadURL(storageRef(storage, raw));
    } catch {
      return "";
    }
  } catch {
    return "";
  }
}

function primaryActorUid(item) {
  const type = String(item.type || "").trim();
  const fromUid = String(item.fromUid || "").trim();
  const visitorUid = String(item.visitorUid || item?.meta?.visitorUid || "").trim();
  const profileUid = String(item.profileUid || item?.meta?.profileUid || "").trim();
  if (type === "visit") return visitorUid || fromUid || profileUid;
  if (type === "follow" || type === "gift") return fromUid || profileUid;
  return fromUid || profileUid || visitorUid;
}

function matchPairUids(item) {
  const m = item?.meta && typeof item.meta === "object" ? item.meta : {};
  const a = String(
    item.peerUid || m.peerUid || m.userUid || item.fromUid || ""
  ).trim();
  const b = String(
    item.peerUid2 || m.peerUid2 || m.otherUid || m.counterpartUid || ""
  ).trim();
  if (a && b) return [a, b];
  const parts = m.participants;
  if (Array.isArray(parts) && parts.length >= 2) {
    return [String(parts[0]).trim(), String(parts[1]).trim()].filter(Boolean);
  }
  const out = [a, b].filter(Boolean);
  return out.length >= 2 ? [out[0], out[1]] : out;
}

function collectUidsNeedingAvatarFetch(items) {
  const set = new Set();
  for (const item of items) {
    const type = String(item.type || "").trim();
    if (type === "match") {
      matchPairUids(item).forEach((uid) => {
        if (uid) set.add(uid);
      });
      continue;
    }
    if (String(item.imageUrl || "").trim()) continue;
    if (type === "visit" || type === "follow" || type === "gift") {
      const u = primaryActorUid(item);
      if (u) set.add(u);
    }
  }
  return [...set];
}

function formatTimeAgo(dateValue, t) {
  try {
    const date =
      typeof dateValue?.toDate === "function"
        ? dateValue.toDate()
        : new Date(dateValue);

    if (!date || Number.isNaN(date.getTime())) return "";

    const diffSec = Math.floor((Date.now() - date.getTime()) / 1000);

    if (diffSec < 60) {
      return t("notifications.now", { defaultValue: "Now" });
    }
    if (diffSec < 3600) {
      return t("notifications.minutes_ago", {
        defaultValue: "{{count}} min ago",
        count: formatNumberEn(Math.floor(diffSec / 60)),
      });
    }
    if (diffSec < 86400) {
      return t("notifications.hours_ago", {
        defaultValue: "{{count}} h ago",
        count: formatNumberEn(Math.floor(diffSec / 3600)),
      });
    }
    if (diffSec < 604800) {
      return t("notifications.days_ago", {
        defaultValue: "{{count}} d ago",
        count: formatNumberEn(Math.floor(diffSec / 86400)),
      });
    }

    return formatDateTimeEn(date, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return "";
  }
}

function getNotificationIcon(type) {
  switch (type) {
    case "follow":
      return <FaUserFriends />;
    case "visit":
      return <FaEye />;
    case "gift":
      return <FaGift />;
    case "withdraw":
      return <FaCoins />;
    case "support":
      return <FaHeadset />;
    case "match":
      return <FaHeart />;
    case "message":
      return <FaComment />;
    case "call":
      return <FaPhone />;
    default:
      return <FaBell />;
  }
}

function getFallbackTitle(type, t) {
  switch (type) {
    case "follow":
      return t("notifications.types.follow", { defaultValue: "New follow" });
    case "visit":
      return t("notifications.types.visit", { defaultValue: "New visit" });
    case "gift":
      return t("notifications.types.gift", { defaultValue: "New gift" });
    case "withdraw":
      return t("notifications.types.withdraw", {
        defaultValue: "Withdrawal update",
      });
    case "support":
      return t("notifications.types.support", { defaultValue: "Support update" });
    case "match":
      return t("notifications.types.match", { defaultValue: "New match" });
    case "message":
      return t("notifications.types.message", { defaultValue: "New message" });
    case "call":
      return t("notifications.types.call", { defaultValue: "Incoming call" });
    default:
      return t("notifications.types.default", { defaultValue: "Notification" });
  }
}

function getCtaLabel(type, t) {
  switch (type) {
    case "visit":
    case "follow":
      return t("notifications.cta.view_profile", {
        defaultValue: "View profile",
      });
    case "gift":
      return t("notifications.cta.open_gift", { defaultValue: "Open gift" });
    case "support":
      return t("notifications.cta.view_chat", {
        defaultValue: "View conversation",
      });
    case "withdraw":
      return t("notifications.cta.view_details", {
        defaultValue: "View details",
      });
    case "match":
      return t("notifications.cta.start_chat", {
        defaultValue: "Start conversation",
      });
    case "message":
      return t("notifications.cta.view_chat", {
        defaultValue: "View conversation",
      });
    case "call":
      return t("notifications.cta.view_call", {
        defaultValue: "Open Livora",
      });
    default:
      return getFallbackTitle(type, t);
  }
}

function getNotificationActorName(item, t) {
  const data = item?.data && typeof item.data === "object" ? item.data : {};
  const meta = item?.meta && typeof item.meta === "object" ? item.meta : {};

  return String(
    item?.actorName ||
      item?.visitorName ||
      item?.fromName ||
      item?.senderName ||
      data.actorName ||
      data.visitorName ||
      data.fromName ||
      data.senderName ||
      meta.actorName ||
      meta.visitorName ||
      meta.fromName ||
      meta.senderName ||
      t("chat.user", { defaultValue: "User" })
  ).trim();
}

function getLocalizedNotificationTitle(item, type, t) {
  if (ALLOWED_TYPES.includes(type)) {
    return getFallbackTitle(type, t);
  }

  return String(item?.title || getFallbackTitle(type, t)).trim();
}

function getLocalizedNotificationBody(item, type, t) {
  const data = item?.data && typeof item.data === "object" ? item.data : {};
  const name = getNotificationActorName(item, t);
  const count = Number(data.count || item?.count || 0);

  switch (type) {
    case "visit":
      if (count > 1) {
        return t("notifications.body.visit_count", {
          defaultValue: "{{name}} viewed your profile {{count}} times today.",
          name,
          count,
        });
      }
      return t("notifications.body.visit", {
        defaultValue: "{{name}} viewed your profile.",
        name,
      });
    case "follow":
      return t("notifications.body.follow", {
        defaultValue: "{{name}} started following you.",
        name,
      });
    case "gift":
      return t("notifications.body.gift", {
        defaultValue: "{{name}} sent you a gift.",
        name,
      });
    case "withdraw":
      return t("notifications.body.withdraw", {
        defaultValue: "Your withdrawal request has been updated.",
      });
    case "support":
      return t("notifications.body.support", {
        defaultValue: "Your support ticket has been updated.",
      });
    case "match":
      return t("notifications.body.match", {
        defaultValue: "A new match is ready for you.",
      });
    case "message":
      return t("notifications.body.message", {
        defaultValue: "New message from {{name}}.",
        name,
      });
    case "call":
      return t("notifications.body.call", {
        defaultValue: "You had an incoming call from {{name}}.",
        name,
      });
    default:
      return String(item?.body || "").trim();
  }
}

function showOnlineDot(item) {
  const m = item?.meta && typeof item.meta === "object" ? item.meta : {};
  if (item?.fromOnline === true || m.online === true || m.isOnline === true)
    return true;
  return false;
}

function resolveActorAvatarUrl(item, avatarByUid) {
  const direct = String(item.imageUrl || "").trim();
  if (direct) return direct;
  const uid = primaryActorUid(item);
  if (uid && avatarByUid[uid]) return avatarByUid[uid];
  return "";
}

/** visit/follow/match: show face photo — resolved URL or gender default (not type glyph). */
function prefersActorPhotoFallback(type) {
  const t = String(type || "").trim();
  return t === "visit" || t === "follow" || t === "match";
}

// ===============================================================
// 🔗 Resolve target page safely
// ===============================================================
function resolveNotificationLink(item) {
  if (!item) return "";

  const directLink = String(item.deepLink || "").trim();
  if (directLink) return directLink;

  const metaDeepLink = String(item?.meta?.deepLink || "").trim();
  if (metaDeepLink) return metaDeepLink;

  const type = String(item.type || "").trim();

  const fromUid = String(item.fromUid || "").trim();
  const visitorUid = String(
    item.visitorUid || item?.meta?.visitorUid || ""
  ).trim();
  const profileUid = String(
    item.profileUid || item?.meta?.profileUid || ""
  ).trim();
  const ticketId = String(item.ticketId || item?.meta?.ticketId || "").trim();
  const withdrawId = String(
    item.withdrawId || item?.meta?.withdrawId || ""
  ).trim();

  switch (type) {
    case "visit":
      return visitorUid
        ? `/user/${visitorUid}`
        : fromUid
        ? `/user/${fromUid}`
        : profileUid
        ? `/user/${profileUid}`
        : "/notifications";

    case "follow":
      return fromUid
        ? `/user/${fromUid}`
        : profileUid
        ? `/user/${profileUid}`
        : "/notifications";

    case "gift":
      return fromUid
        ? `/user/${fromUid}`
        : profileUid
        ? `/user/${profileUid}`
        : "/notifications";

    case "support":
      return ticketId ? `/support/chat/${ticketId}` : "/support";

    case "withdraw":
      return withdrawId ? `/withdraw-history` : "/withdraw-history";

    case "match": {
      const chatId = String(item.chatId || item?.meta?.chatId || "").trim();
      const otherUid = String(
        item.otherUid || item?.meta?.otherUid || primaryActorUid(item) || ""
      ).trim();
      const chatRoute = chatId ? buildExistingChatRoute(chatId) : null;
      if (chatRoute) return chatRoute;
      if (otherUid) return `/user/${otherUid}`;
      return "/match-radar";
    }

    // The server (chatCore.js) always sets an explicit deepLink for
    // "message" notifications, so this branch is a defensive fallback
    // only - it is never reached in normal operation, only for a
    // malformed/legacy document missing that field.
    case "message": {
      const chatId = String(item.chatId || item?.meta?.chatId || "").trim();
      const chatRoute = chatId ? buildExistingChatRoute(chatId) : null;
      return chatRoute || "/notifications";
    }

    // Deliberately no dedicated "call" case: no incoming-call push is
    // currently sent (see the worktree's stop report), and this batch does
    // not introduce call-specific routing. Falls through to the safe
    // default below.

    default:
      return "/notifications";
  }
}

// Exported for focused unit testing of pure presentation logic only (no
// Firestore/router rendering required) - see NotificationsPage.test.js.
export {
  ALLOWED_TYPES,
  getFallbackTitle,
  getNotificationIcon,
  getLocalizedNotificationBody,
  getCtaLabel,
  resolveNotificationLink,
};

export default function NotificationsPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [fetchKey, setFetchKey] = useState(0);
  const [busyId, setBusyId] = useState("");
  const [activeTab, setActiveTab] = useState("all");
  const [avatarByUid, setAvatarByUid] = useState({});

  const autoReadDoneRef = useRef(false);
  const avatarMapRef = useRef({});

  useEffect(() => {
    avatarMapRef.current = avatarByUid;
  }, [avatarByUid]);

  // ===============================================================
  // 🔥 Realtime fetch (unchanged query)
  // ===============================================================
  useEffect(() => {
    const user = auth.currentUser;
    if (!user) {
      setLoading(false);
      setLoadError(false);
      return;
    }

    setLoadError(false);

    const q = query(
      collection(db, "notifications"),
      where("uid", "==", user.uid),
      orderBy("createdAt", "desc")
    );

    const unsub = onSnapshot(
      q,
      (snap) => {
        const list = snap.docs
          .map((d) => ({
            id: d.id,
            ...d.data(),
          }))
          .filter((item) =>
            ALLOWED_TYPES.includes(String(item.type || "").trim())
          );

        setItems(list);
        setLoadError(false);
        setLoading(false);
      },
      (err) => {
        console.error("notifications snapshot error:", err);
        setLoadError(true);
        setLoading(false);
      }
    );

    return () => unsub();
  }, [fetchKey]);

  const handleRetryNotifications = () => {
    setLoadError(false);
    setLoading(true);
    setFetchKey((key) => key + 1);
  };

  // ===============================================================
  // Avatar URLs: fetch only missing uids from current items (cached)
  // ===============================================================
  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      const need = collectUidsNeedingAvatarFetch(items).filter(
        (uid) => uid && !avatarMapRef.current[uid]
      );
      if (!need.length) return;

      const pairs = await Promise.all(
        need.map(async (uid) => [uid, await fetchUserAvatarUrl(uid)])
      );

      if (cancelled) return;

      setAvatarByUid((prev) => {
        const next = { ...prev };
        for (const [uid, url] of pairs) {
          if (url) next[uid] = url;
        }
        return next;
      });
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [items]);

  // ===============================================================
  // ✅ Auto mark all read when page is opened
  // ===============================================================
  useEffect(() => {
    if (loading) return;
    if (!items.length) return;
    if (autoReadDoneRef.current) return;

    const unreadItems = items.filter((item) => item.read !== true);
    if (!unreadItems.length) {
      autoReadDoneRef.current = true;
      return;
    }

    autoReadDoneRef.current = true;

    setItems((prev) =>
      prev.map((x) => ({
        ...x,
        read: true,
      }))
    );

    markNotificationsReadBatched(
      db,
      unreadItems.map((item) => item.id)
    ).catch((e) => {
      console.error("auto mark read error:", e);

      autoReadDoneRef.current = false;

      setItems((prev) =>
        prev.map((x) => {
          const original = items.find((it) => it.id === x.id);
          return original ? { ...x, read: original.read } : x;
        })
      );
    });
  }, [items, loading]);

  // ===============================================================
  // 🎯 Filter by tab
  // ===============================================================
  const filteredItems = useMemo(() => {
    if (activeTab === "all") return items;
    return items.filter((item) => String(item.type || "").trim() === activeTab);
  }, [items, activeTab]);

  const unreadCount = useMemo(
    () => items.filter((item) => item.read !== true).length,
    [items]
  );

  // ===============================================================
  // ✅ Open notification
  // ===============================================================
  const handleOpenNotification = async (item) => {
    try {
      if (!item?.id) return;
      if (busyId) return;

      setBusyId(item.id);

      if (item.read !== true) {
        setItems((prev) =>
          prev.map((x) =>
            x.id === item.id
              ? {
                  ...x,
                  read: true,
                }
              : x
          )
        );

        await updateDoc(doc(db, "notifications", item.id), {
          read: true,
        });
      }

      const targetLink = resolveNotificationLink(item);

      if (targetLink) {
        setTimeout(() => {
          navigate(targetLink);
        }, 80);
      }
    } catch (e) {
      console.error("open notification error:", e);
    } finally {
      setBusyId("");
    }
  };

  // ===============================================================
  // ✅ Mark all read
  // ===============================================================
  const handleMarkAllRead = async () => {
    try {
      const unreadItems = items.filter((item) => item.read !== true);
      if (!unreadItems.length) return;

      setItems((prev) =>
        prev.map((x) => ({
          ...x,
          read: true,
        }))
      );

      await markNotificationsReadBatched(
        db,
        unreadItems.map((item) => item.id)
      );
    } catch (e) {
      console.error("mark all read error:", e);
    }
  };

  const actorGender = (item) =>
    String(item?.meta?.fromGender || item?.meta?.gender || "").trim();

  return (
    <div className="notifications-page">
      <div className="notifications-bg-glow" aria-hidden />
      <div className="notifications-overlay" />

      <div className="notifications-shell">
        <div className="notifications-header-hero">
          <div className="notifications-header-glow" aria-hidden />
          <header className="notifications-header">
            <button
              type="button"
              className="notifications-settings-btn"
              onClick={() => navigate("/settings")}
              aria-label={t("notifications.settings_aria", {
                defaultValue: "Settings",
              })}
            >
              <FaCog aria-hidden />
            </button>
            <div className="notifications-head-text">
              <h1 className="notifications-title">
                {t("notifications.title", { defaultValue: "Notifications" })}
              </h1>
              <p className="notifications-subtitle">
                {unreadCount > 0
                  ? t("notifications.unread_count", {
                      defaultValue: "{{count}} unread",
                      count: formatNumberEn(unreadCount),
                    })
                  : t("notifications.all_read", {
                      defaultValue: "All notifications are read",
                    })}
              </p>
            </div>
            <div className="notifications-icon-main">
              <FaBell aria-hidden />
              {unreadCount > 0 ? (
                <span className="notifications-bell-dot" aria-hidden />
              ) : null}
            </div>
          </header>
        </div>

        <button
          type="button"
          className="notifications-read-all-btn"
          onClick={handleMarkAllRead}
          disabled={!unreadCount}
        >
          <FaCheckDouble aria-hidden />
          <span>
            {t("notifications.mark_all_read", {
              defaultValue: "Mark all as read",
            })}
          </span>
        </button>

        <div className="notifications-tabs" role="tablist">
          {TABS.map((tab) => {
            const TabIcon = tab.Icon;
            return (
              <button
                key={tab.key}
                type="button"
                role="tab"
                aria-selected={activeTab === tab.key}
                className={`notifications-tab-btn ${
                  activeTab === tab.key ? "active" : ""
                }`}
                onClick={() => setActiveTab(tab.key)}
              >
                <TabIcon className="notifications-tab-btn__icon" aria-hidden />
                <span className="notifications-tab-btn__label">
                  {t(`notifications.tabs.${tab.key}`, {
                    defaultValue: tab.defaultValue,
                  })}
                </span>
              </button>
            );
          })}
        </div>

        <main className="notifications-content">
          {loadError ? (
            <div className="notifications-empty">
              <div className="notifications-empty-icon">
                <FaBell aria-hidden />
              </div>
              <h3 className="notifications-empty-title">
                {t("notifications.load_error_title", {
                  defaultValue: "Couldn't load notifications",
                })}
              </h3>
              <p className="notifications-empty-desc">
                {t("notifications.load_error_subtitle", {
                  defaultValue: "Check your connection and try again.",
                })}
              </p>
              <button
                type="button"
                className="notifications-read-all-btn"
                onClick={handleRetryNotifications}
              >
                {t("notifications.retry", { defaultValue: "Try again" })}
              </button>
            </div>
          ) : loading ? (
            <div className="notifications-empty">
              <div className="notifications-empty-icon">
                <FaBell aria-hidden />
              </div>
              <h3 className="notifications-empty-title">
                {t("notifications.loading", {
                  defaultValue: "Loading notifications...",
                })}
              </h3>
            </div>
          ) : filteredItems.length === 0 ? (
            <div className="notifications-empty">
              <div className="notifications-empty-icon">
                <FaBell aria-hidden />
              </div>
              <h3 className="notifications-empty-title">
                {t("notifications.empty_title", {
                  defaultValue: "No notifications yet",
                })}
              </h3>
              <p className="notifications-empty-desc">
                {t("notifications.empty_subtitle", {
                  defaultValue:
                    "When something important happens, it will appear here.",
                })}
              </p>
            </div>
          ) : (
            <div className="notifications-list">
              {filteredItems.map((item) => {
                const type = String(item.type || "").trim();
                const title = getLocalizedNotificationTitle(item, type, t);
                const body = getLocalizedNotificationBody(item, type, t);
                const isUnread = item.read !== true;
                const onlineDot = showOnlineDot(item);
                const isMatch = type === "match";

                const pairs = isMatch ? matchPairUids(item) : [];
                const uidA = pairs[0] || "";
                const uidB = pairs[1] || "";
                const urlA =
                  String(item.imageUrl || "").trim() ||
                  (uidA ? avatarByUid[uidA] : "") ||
                  "";
                const urlB = uidB ? avatarByUid[uidB] : "";

                const resolvedAvatar = resolveActorAvatarUrl(item, avatarByUid);
                const genderFallback = actorGender(item) || "male";

                return (
                  <button
                    key={item.id}
                    type="button"
                    className={`notification-card ${isUnread ? "unread" : "read"} type-accent-${type} ${
                      isMatch ? "notification-card--match" : ""
                    }`}
                    onClick={() => handleOpenNotification(item)}
                    disabled={busyId === item.id}
                  >
                    <div className="notification-card__rail">
                      <span className="notification-time">
                        {formatTimeAgo(item.createdAt, t)}
                      </span>
                      <span
                        className={`notification-card__action-icon type-${type}`}
                      >
                        {isMatch ? (
                          <span className="notification-card__action-hearts">
                            <FaHeart />
                            <FaHeart />
                            <FaHeart />
                          </span>
                        ) : (
                          getNotificationIcon(type)
                        )}
                      </span>
                    </div>

                    <div className="notification-card__main">
                      <h3 className="notification-title">{title}</h3>
                      {body ? (
                        <p className="notification-text">{body}</p>
                      ) : null}
                      <span className={`notification-cta-pill type-${type}`}>
                        {getCtaLabel(type, t)}
                      </span>
                    </div>

                    <div className="notification-card__avatar-wrap">
                      {isMatch ? (
                        <div
                          className="notification-card__match-avatars"
                          aria-hidden
                        >
                          <img
                            src={
                              urlA ||
                              defaultAvatarByGender(
                                item?.meta?.peerGender || genderFallback
                              )
                            }
                            alt=""
                            className="notification-card__match-face notification-card__match-face--a"
                            onError={(e) => {
                              e.currentTarget.src =
                                defaultAvatarByGender("male");
                            }}
                          />
                          <span className="notification-card__match-heart">
                            <FaHeart aria-hidden />
                          </span>
                          <img
                            src={
                              urlB ||
                              defaultAvatarByGender(
                                item?.meta?.peerGender2 || genderFallback
                              )
                            }
                            alt=""
                            className="notification-card__match-face notification-card__match-face--b"
                            onError={(e) => {
                              e.currentTarget.src =
                                defaultAvatarByGender("female");
                            }}
                          />
                        </div>
                      ) : prefersActorPhotoFallback(type) ? (
                        <img
                          src={
                            resolvedAvatar ||
                            defaultAvatarByGender(genderFallback || "male")
                          }
                          alt=""
                          className="notification-avatar"
                          onError={(e) => {
                            e.currentTarget.src =
                              defaultAvatarByGender(genderFallback);
                          }}
                        />
                      ) : resolvedAvatar ? (
                        <img
                          src={resolvedAvatar}
                          alt=""
                          className="notification-avatar"
                          onError={(e) => {
                            e.currentTarget.src =
                              defaultAvatarByGender(genderFallback);
                          }}
                        />
                      ) : (
                        <div
                          className={`notification-type-icon type-${type}`}
                          aria-hidden
                        >
                          {getNotificationIcon(type)}
                        </div>
                      )}
                      {!isMatch && onlineDot ? (
                        <span className="notification-online-dot" aria-hidden />
                      ) : null}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </main>
      </div>

      <BottomNav active="notifications" />
    </div>
  );
}
